/**
 * @file pages/BuildLog.js
 * @description The Build Log — a public developer journal telling the story
 * of building Inspection Butler, day by day, from both perspectives:
 * James (the blue-collar entrepreneur) and Claude (the AI developer).
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { entries } from '../data/buildLogEntries';

// ----------------------------------------------------------
// Phase data for the progress tracker
// ----------------------------------------------------------
// Status: 'active' shows a blue dot + progress bar. 'locked' shows a gray dot.
// Phases past 100% display the same as 100% active. Phase 8 stays locked.
const phases = [
  { num: 1, name: 'Foundation', weeks: '1-4', status: 'active', pct: 100 },
  { num: 2, name: 'Inspection Engine', weeks: '5-10', status: 'active', pct: 100 },
  { num: 3, name: 'Business Mgmt', weeks: '11-16', status: 'active', pct: 70 },
  { num: 4, name: 'Field Ops', weeks: '17-20', status: 'active', pct: 100 },
  { num: 5, name: 'AI & Reports', weeks: '21-24', status: 'active', pct: 100 },
  { num: 6, name: 'Internal Beta', weeks: '25-28', status: 'active', pct: 80 },
  { num: 7, name: 'Launch Prep', weeks: '29-34', status: 'active', pct: 75 },
  { num: 8, name: 'National Release', weeks: '35+', status: 'locked', pct: 0 },
];

// ----------------------------------------------------------
// Progress Tracker component
// ----------------------------------------------------------
function ProgressTracker() {
  return (
    <div className="blog-progress">
      <h3 style={{ marginBottom: '0.5rem', fontSize: '0.875rem', textTransform: 'uppercase', letterSpacing: '0.05em', color: '#999' }}>
        Where we are
      </h3>
      <div className="phase-track">
        {phases.map((p) => (
          <div key={p.num} className={`phase-node ${p.status}`}>
            <div className="phase-dot">
              {p.status === 'active' && <div className="phase-pulse" />}
              <span>{p.num}</span>
            </div>
            <div className="phase-label">{p.name}</div>
            {p.status === 'active' && (
              <div className="phase-bar-wrap">
                <div className="phase-bar-fill" style={{ width: `${p.pct}%` }} />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ----------------------------------------------------------
// Single blog entry component
// ----------------------------------------------------------
function BlogEntry({ entry }) {
  const [expanded, setExpanded] = React.useState(true);

  return (
    <article className="blog-entry">
      <div className="blog-entry-header" onClick={() => setExpanded(!expanded)} style={{ cursor: 'pointer' }}>
        <div className="blog-date-badge">
          <span className="blog-day">{entry.day}</span>
          <span className="blog-month">{entry.month}</span>
        </div>
        <div>
          <h2 className="blog-title">{entry.title}</h2>
          <p className="blog-subtitle">{entry.subtitle}</p>
        </div>
        <span className="blog-expand">{expanded ? '\u2212' : '+'}</span>
      </div>

      {expanded && (
        <div className="blog-entry-body">
          <div className="blog-perspective james">
            <div className="blog-avatar">
              <span className="avatar-icon">JB</span>
              <span className="avatar-label">James</span>
              <span className="avatar-role">The Builder</span>
            </div>
            <div className="blog-text" dangerouslySetInnerHTML={{ __html: entry.james }} />
          </div>

          <div className="blog-divider">
            <span>meanwhile</span>
          </div>

          <div className="blog-perspective claude">
            <div className="blog-avatar">
              <span className="avatar-icon">C</span>
              <span className="avatar-label">Claude</span>
              <span className="avatar-role">The Code</span>
            </div>
            <div className="blog-text" dangerouslySetInnerHTML={{ __html: entry.claude }} />
          </div>

          {entry.milestones && entry.milestones.length > 0 && (
            <div className="blog-milestones">
              <h4>Milestones hit</h4>
              <div className="milestone-tags">
                {entry.milestones.map((m, i) => (
                  <span key={i} className="milestone-tag">{m}</span>
                ))}
              </div>
            </div>
          )}

          {entry.stats && (
            <div className="blog-stats">
              {entry.stats.map((s, i) => (
                <div key={i} className="blog-stat">
                  <span className="stat-num">{s.value}</span>
                  <span className="stat-label">{s.label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

// ----------------------------------------------------------
// Main BuildLog page
// ----------------------------------------------------------
function BuildLog() {
  return (
    <div className="blog-page">
      <div className="blog-hero">
        <Link to="/" className="blog-back-link">&larr; Inspection Butler</Link>
        <h1>The Build Log</h1>
        <p className="blog-hero-sub">
          A septic inspector and an AI walk into a codebase.<br />
          This is what happened.
        </p>
        <p className="blog-hero-desc">
          This is the real, unfiltered story of building Inspection Butler from scratch.
          No developers. No investors. No clue what we're doing half the time.
          Just a guy who's tired of bad software and an AI that types really fast.
        </p>
      </div>

      <ProgressTracker />

      <div className="blog-entries">
        {entries.map((entry, i) => (
          <BlogEntry key={i} entry={entry} />
        ))}
      </div>

      <div className="blog-footer">
        <p>New entries posted after each build session.</p>
        <p style={{ color: '#999', fontSize: '0.813rem', marginTop: '0.5rem' }}>
          No passwords, API keys, or security details are shared here. Just the story.
        </p>
      </div>
    </div>
  );
}

export default BuildLog;
